import { getOrigin } from "@website/utils/channel/get-origin";
import { deepCompare } from "@website/utils/deep-compare";
import { numberConvert } from "@website/utils/number-convert";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { ReserveRateFooter } from "@website/widget/reserve-rate/reserve-rate-footer";
import { ReserveRateHeader } from "@website/widget/reserve-rate/reserve-rate-header";
import { ReserveProgressBar } from "@website/widget/reserve-rate/reserve-rate-progress-bar";
import {
  ReserveRateEnum,
  ReserveRatePropertiesTypes,
  ReserveRateTypes,
  ReserveVillaRateEnum
} from "@website/widget/reserve-rate/reserve-rate.types";
import toString from "lodash-es/toString";
import { useRouter } from "next/router";
import { memo } from "react";

const Component = (properties: ReserveRatePropertiesTypes) => {
  const {
    rate,
    rateCount,
    offerCount,
    offerMessage,
    rateDetail,
    showTitle = true,
    size = "large",
    hasBackground = true,
    hotelType,
    pageTitle,
    pageDescription,
    aiSummary,
    showFooter = true
  } = properties;
  const router = useRouter();

  if (!rateCount || !rate) return null;
  return (
    <div
      className={`${
        size === "medium"
          ? "bg-dim-background h-fit min-w-[324px] rounded-lg p-4 xl:bg-white"
          : "min-w-[312px]"
      } ${aiSummary ? "" : "!w-full"}`}
    >
      {pageTitle && pageDescription && (
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Review",
              author: {
                "@type": "Person",
                name: replaceSnappTripBasedOnChannel("اسنپ تریپ")
              },
              itemReviewed: {
                "@type": "Product",
                name: toString(pageTitle),
                image: `${getOrigin()}/logo.png`,
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: toString(rate ?? 1),
                  ratingCount: toString(rateCount ?? 1),
                  reviewCount: toString(offerCount ?? 1),
                  bestRating: "5",
                  worstRating: "1"
                },
                description: toString(pageDescription),
                brand: replaceSnappTripBasedOnChannel("اسنپ تریپ")
              },
              reviewRating: {
                "@type": "Rating",
                ratingValue: toString(rate ?? 1),
                bestRating: "5",
                worstRating: "1"
              }
            })
          }}
          type="application/ld+json"
        />
      )}
      {showTitle && (
        <h2 className="text-subtitle-1 md:text-headline-6 mb-4">
          {replaceSnappTripBasedOnChannel(
            `امتیاز ${hotelType} از دید کاربران اسنپ‌تریپ`
          )}
        </h2>
      )}
      <div
        className={`flex min-w-[218px] flex-col gap-4 rounded-[8px] md:w-full lg:max-w-[1200px] xl:rounded-[8px] ${
          size === "large"
            ? "xl:max-w-[1200px] xl:rounded-[8px] xl:px-10 xl:py-8"
            : ""
        } ${hasBackground ? "bg-dim-background p-6 xl:px-10 xl:py-8" : ""}`}
      >
        <div
          className={`flex flex-col gap-4 ${
            size === "large" ? "xl:flex-row xl:items-center xl:gap-[40px]" : ""
          }`}
        >
          <ReserveRateHeader properties={{ rate, rateCount, size }} />
          <div
            className={`bg-outline-thin hidden h-[72px] w-px ${
              size === "large" ? "xl:block" : ""
            }`}
          />
          <div
            className={`grid w-full gap-x-[40px] gap-y-4 lg:gap-y-1 ${
              size === "large" ? "xl:grid-cols-2" : ""
            }`}
          >
            {Object.keys(rateDetail)?.map((key) => {
              if (!rateDetail[key as ReserveRateTypes]) return null;
              return (
                <div
                  className="flex flex-row items-start gap-4 lg:flex-col lg:gap-1"
                  key={key}
                >
                  <div className="text-caption md:text-body-2 w-[152px] md:w-[178px]">
                    {process.env.NEXT_PUBLIC_APP_NAME === "villa"
                      ? ReserveVillaRateEnum[key as ReserveRateTypes]
                      : ReserveRateEnum[key as ReserveRateTypes]}
                  </div>
                  <div className="flex w-full flex-row items-center gap-1">
                    <ReserveProgressBar
                      properties={{
                        limit: 5,
                        progress: Number(rateDetail[key as ReserveRateTypes])
                      }}
                    />
                    <div className="text-caption md:text-body-2">
                      {numberConvert(rateDetail[key as ReserveRateTypes], {
                        locale: router.locale
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {Boolean(offerCount) && showFooter && (
        <ReserveRateFooter properties={{ offerCount, offerMessage }} />
      )}
    </div>
  );
};
export const ReserveRate = memo(Component, deepCompare);
